import React, { useState, useEffect } from "react";
import { FaFilePdf, FaArrowRight, FaHandPointUp, FaFire } from "react-icons/fa";
import { GrLocation } from "react-icons/gr";
import { PiBedLight } from "react-icons/pi";
import { FaProcedures } from "react-icons/fa";
import { useParams, useNavigate } from "react-router-dom";
import { MdOutlinePhone } from "react-icons/md";

// --- API Endpoints ---
const NEW_API_URL =
  "https://dev.mithahomes.com/backend/api/mainprojectprocess/maincard";
const IMG_BASE_URL = "https://dev.mithahomes.com/backend";

const COLORS = {
  primaryOrange: "#e46324",
  primaryGreen: "#5ec250",
  primaryDarkBlue: "#016563",
  textOrange: "#e46d3e",
  cardBg: "#ffffff",
  borderColor: "#e0e0e0",
  lightGray: "#f0f0f0",
  darkGray: "#333",
  mediumDarkGray: "#555",
  newTagRed: "#ff4444",
  disabledGray: "#cccccc",
};

// Define the priority order for sorting based on your requirements
const CATEGORY_PRIORITY = {
  "Upcoming Projects": 1, // First
  "Ongoing Projects": 2, // Second
  "Ready to Occupy": 3, // Third
  "Completed Projects": 4, // Fourth
};

// Define the fixed ranking order for specific projects (to be displayed LAST)
const RANKED_PROJECTS = [
  "Mitha Haritham",
  "Mitha Vriksham",
  "Mitha Premam",
  "Mitha Maanasa",
  "Mitha Twins",
  "Mitha Brindhavan",
];

// These projects should only show if they are "Completed Projects"
const COMPLETED_RANKED_PROJECTS = RANKED_PROJECTS.map((project) =>
  project.toLowerCase(),
);

const NEW_PROJECT_IDS = []; // Add your new project IDs here, e.g., [79, 80]

const styles = {
  mainContainerStyle: {
    fontFamily: '"Roboto", system-ui, -apple-system, sans-serif',
    backgroundColor: COLORS.lightGray,
    padding: "20px 15px",
    minHeight: "100vh",
  },
  gridStyle: {
    display: "grid",
    gap: "25px",
    maxWidth: "1200px",
    margin: "0 auto",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
  },
  cardContainerStyle: {
    position: "relative",
    backgroundColor: COLORS.cardBg,
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
    overflow: "hidden",
    transition: "transform 0.3s, box-shadow 0.3s",
    display: "flex",
    flexDirection: "column",
    borderRadius: "8px",
    height: "100%",
    cursor: "pointer",
  },
  titleStyle: {
    color: "#333",
    fontSize: "18px",
    margin: "5px 15px 0",
    fontWeight: 700,
    textAlign: "center",
    paddingTop: "20px",
  },
  locationStyle: {
    fontSize: "0.95rem",
    fontWeight: 400,
    padding: "5px",
    color: "#777",
    textAlign: "center",
    margin: "0 15px 10px",
  },
  imageContainerStyle: {
    position: "relative",
    width: "100%",
    backgroundColor: "#fff",
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  promoImageStyle: {
    width: "100%",
    height: "auto",
    maxHeight: "500px",
    objectFit: "contain",
    display: "block",
    transition: "transform 0.5s ease",
  },
  detailsSectionStyle: {
    padding: "15px",
    borderBottom: `1px solid ${COLORS.borderColor}`,
    flexGrow: 1,
  },
  detailsTitleStyle: {
    fontSize: "1.1em",
    margin: "0 0 12px",
    fontWeight: 700,
    color: COLORS.darkGray,
  },
  detailItemStyle: {
    display: "flex",
    alignItems: "center",
    marginBottom: "8px",
    gap: "10px",
  },
  detailPStyle: {
    margin: 0,
    fontSize: "14px",
    color: COLORS.mediumDarkGray,
  },
  detailIconStyle: {
    color: "#af9c78",
    fontSize: "22px",
    display: "flex",
  },
  actionsSectionStyle: {
    display: "flex",
    justifyContent: "space-between",
    padding: "12px",
    gap: "8px",
    flexWrap: "wrap",
  },
  btnBaseStyle: {
    flex: "1 1 0px",
    minWidth: "80px",
    display: "flex",
    borderRadius: "5px",
    alignItems: "center",
    justifyContent: "center",
    padding: "10px 5px",
    textDecoration: "none",
    color: "white",
    fontWeight: 500,
    fontSize: "0.75rem",
    transition: "opacity 0.2s, background-color 0.2s",
    cursor: "pointer",
    border: "none",
    outline: "none",
  },
  disabledBtnStyle: {
    backgroundColor: COLORS.disabledGray,
    cursor: "not-allowed",
    opacity: 0.6,
  },
  viewMoreBtnStyle: { backgroundColor: COLORS.primaryOrange },
  brochureBtnStyle: { backgroundColor: COLORS.primaryGreen },
  getDirectionBtnStyle: { backgroundColor: COLORS.primaryDarkBlue },
  btnIconStyle: { marginRight: "10px" },
  newTagStyle: {
    position: "absolute",
    top: "10px",
    right: "10px",
    backgroundColor: COLORS.newTagRed,
    color: "white",
    padding: "4px 10px",
    borderRadius: "4px",
    fontSize: "12px",
    fontWeight: "bold",
    zIndex: 10,
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  headerStyle: {
    textAlign: "center",
    marginBottom: "30px",
  },
  titleHeaderStyle: {
    fontSize: "28px",
    fontWeight: "700",
    color: COLORS.darkGray,
    marginBottom: "10px",
  },
  idDisplayStyle: {
    fontSize: "16px",
    color: COLORS.primaryOrange,
    fontWeight: "600",
    backgroundColor: "white",
    padding: "8px 15px",
    borderRadius: "5px",
    display: "inline-block",
    boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
  },
  emptyStateStyle: {
    textAlign: "center",
    padding: "50px 20px",
    color: COLORS.mediumDarkGray,
    fontSize: "18px",
    gridColumn: "1 / -1",
  },
};

const CardWithShare = ({ project }) => {
  const [isHovered, setIsHovered] = useState(false);
  const projectUrl = `https://mithahomes.com/project/${project.pid}`;
  const isNew = NEW_PROJECT_IDS.includes(Number(project.pid));

  // Check if brochure is null or empty
  const hasBrochure = project.brochure && project.brochure.trim() !== "";
  const hasDirection =
    project.get_direction && project.get_direction.trim() !== "";

  // Handle card click to navigate to project page
  const handleCardClick = (e) => {
    e.preventDefault();
    window.open(projectUrl, "_blank");
  };

  // Handle View More button click
  const handleViewMoreClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    window.open(projectUrl, "_blank");
  };

  // Handle Contact button click
  const handleContactClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    window.location.href = "/contact";
  };

  // Handle brochure button click
  const handleBrochureClick = (e) => {
    e.stopPropagation();
  };

  // Handle direction button click
  const handleDirectionClick = (e) => {
    e.stopPropagation();
    // Let the anchor tag handle the navigation
  };

  return (
    <div
      style={{ height: "100%" }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleCardClick}
      role="button"
      tabIndex={0}
      onKeyPress={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          handleCardClick(e);
        }
      }}
    >
      <div
        style={{
          ...styles.cardContainerStyle,
          transform: isHovered ? "translateY(-5px)" : "none",
          boxShadow: isHovered
            ? "0 8px 20px rgba(0, 0, 0, 0.15)"
            : "0 4px 12px rgba(0, 0, 0, 0.1)",
        }}
      >
        {isNew && (
          <div style={styles.newTagStyle}>
            <FaFire /> NEW
          </div>
        )}

        <div style={{ textAlign: "center" }}>
          <h4
            style={{
              ...styles.titleStyle,
              color: isHovered ? COLORS.textOrange : "#333",
            }}
          >
            {project.project_name || "Project Name"}
          </h4>
          <h6
            style={{
              ...styles.locationStyle,
              color: isHovered ? COLORS.textOrange : "#777",
            }}
          >
            {project.location || "Location"}
          </h6>
        </div>

        <div style={styles.imageContainerStyle}>
          <img
            src={IMG_BASE_URL + (project.banner_image || "")}
            alt={project.project_name}
            style={{
              ...styles.promoImageStyle,
              transform: isHovered ? "scale(1.02)" : "scale(1)",
            }}
            onError={(e) => {
              e.target.src =
                "https://via.placeholder.com/400x250?text=Project+Image";
            }}
            loading="lazy"
          />
        </div>

        <div style={styles.detailsSectionStyle}>
          <h2 style={styles.detailsTitleStyle}>{project.project_name}</h2>
          <div style={styles.detailItemStyle}>
            <span style={styles.detailIconStyle}>
              <GrLocation />
            </span>
            <p style={styles.detailPStyle}>
              <strong>{project.location}</strong>
            </p>
          </div>
          <div style={styles.detailItemStyle}>
            <span style={styles.detailIconStyle}>
              <PiBedLight />
            </span>
            <p style={styles.detailPStyle}>
              <strong>{project.project_caption}</strong>
            </p>
          </div>
          <div style={styles.detailItemStyle}>
            <span style={styles.detailIconStyle}>
              <FaProcedures />
            </span>
            <p style={styles.detailPStyle}>
              <strong>{project.category_id}</strong>
            </p>
          </div>
        </div>

        <div style={styles.actionsSectionStyle}>
          <button
            style={{ ...styles.btnBaseStyle, ...styles.viewMoreBtnStyle }}
            onClick={handleViewMoreClick}
          >
            <span style={styles.btnIconStyle}>
              <FaHandPointUp />
            </span>
            View More
          </button>

          {hasBrochure ? (
            <a
              href={project.brochure}
              style={{ ...styles.btnBaseStyle, ...styles.brochureBtnStyle }}
              onClick={handleBrochureClick}
              target="_blank"
              rel="noopener noreferrer"
            >
              <span style={styles.btnIconStyle}>
                <FaFilePdf />
              </span>
              Brochure
            </a>
          ) : (
            <button
              style={{ ...styles.btnBaseStyle, ...styles.brochureBtnStyle }}
              onClick={handleContactClick}
            >
              <span style={styles.btnIconStyle}>
                <MdOutlinePhone />
              </span>
              Contact
            </button>
          )}

          {hasDirection ? (
            <a
              href={project.get_direction}
              style={{ ...styles.btnBaseStyle, ...styles.getDirectionBtnStyle }}
              onClick={handleDirectionClick}
              target="_blank"
              rel="noopener noreferrer"
            >
              <span style={styles.btnIconStyle}>
                <FaArrowRight />
              </span>
              Direction
            </a>
          ) : (
            <button
              style={{ ...styles.btnBaseStyle, ...styles.disabledBtnStyle }}
              disabled
            >
              <span style={styles.btnIconStyle}>
                <FaArrowRight />
              </span>
              Direction
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const ProjectCardsGrid = () => {
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get the current project ID from URL parameters
  const { id: currentProjectId } = useParams();

  useEffect(() => {
    const fetchAllProjects = async () => {
      try {
        const response = await fetch(NEW_API_URL);
        if (!response.ok)
          throw new Error(`HTTP error! status: ${response.status}`);
        const result = await response.json();

        if (result.success && Array.isArray(result.data)) {
          // Filter out the current project based on ID
          const filteredProjects = result.data.filter((project) => {
            // Skip the current project if currentProjectId is defined
            if (currentProjectId && project.pid == currentProjectId) {
              return false;
            }

            const projectNameLower = (project.project_name || "").toLowerCase();

            // Check if this is one of our ranked projects
            const isRankedProject = COMPLETED_RANKED_PROJECTS.some(
              (rankedProject) =>
                projectNameLower.includes(rankedProject.toLowerCase()),
            );

            // If it's a ranked project, only include if it's "Completed Projects"
            if (isRankedProject) {
              return project.category_id === "Completed Projects";
            }

            return true;
          });

          console.log(
            `Filtered out project with ID ${currentProjectId}. Showing ${filteredProjects.length} projects.`,
          );

          // Separate projects into categories
          const rankedProjects = [];
          const otherProjects = [];
          const newProjects = [];

          filteredProjects.forEach((project) => {
            const projectNameLower = (project.project_name || "").toLowerCase();
            const isRankedProject = COMPLETED_RANKED_PROJECTS.some(
              (rankedProject) =>
                projectNameLower.includes(rankedProject.toLowerCase()),
            );

            if (isRankedProject) {
              rankedProjects.push(project);
            } else if (NEW_PROJECT_IDS.includes(Number(project.pid))) {
              newProjects.push(project);
            } else {
              otherProjects.push(project);
            }
          });

          // Sort other projects by rank if available, otherwise by category priority then alphabetically
          const sortedOtherProjects = otherProjects.sort((a, b) => {
            // First try to sort by rank field if available
            const rankA = a.rank || a.rankorder || a.order || 999;
            const rankB = b.rank || b.rankorder || b.order || 999;

            if (rankA !== rankB) {
              return rankA - rankB;
            }

            // If ranks are equal or not available, sort by category priority
            const priorityA = CATEGORY_PRIORITY[a.category_id] || 99;
            const priorityB = CATEGORY_PRIORITY[b.category_id] || 99;

            if (priorityA !== priorityB) {
              return priorityA - priorityB;
            }

            // If same category, sort alphabetically by project name
            return (a.project_name || "").localeCompare(b.project_name || "");
          });

          // Sort new projects by rank if available, otherwise by category priority then alphabetically
          const sortedNewProjects = newProjects.sort((a, b) => {
            // First try to sort by rank field if available
            const rankA = a.rank || a.rankorder || a.order || 999;
            const rankB = b.rank || b.rankorder || b.order || 999;

            if (rankA !== rankB) {
              return rankA - rankB;
            }

            // If ranks are equal or not available, sort by category priority
            const priorityA = CATEGORY_PRIORITY[a.category_id] || 99;
            const priorityB = CATEGORY_PRIORITY[b.category_id] || 99;

            if (priorityA !== priorityB) {
              return priorityA - priorityB;
            }

            // If same category, sort alphabetically by project name
            return (a.project_name || "").localeCompare(b.project_name || "");
          });

          // Sort ranked projects according to RANKED_PROJECTS order
          const sortedRankedProjects = rankedProjects.sort((a, b) => {
            const indexA = RANKED_PROJECTS.findIndex((rankedProject) =>
              (a.project_name || "")
                .toLowerCase()
                .includes(rankedProject.toLowerCase()),
            );
            const indexB = RANKED_PROJECTS.findIndex((rankedProject) =>
              (b.project_name || "")
                .toLowerCase()
                .includes(rankedProject.toLowerCase()),
            );

            // If both are in the ranked list, sort by the list order
            if (indexA !== -1 && indexB !== -1) {
              return indexA - indexB;
            }

            // If only one is in the ranked list, it comes first
            if (indexA !== -1) return -1;
            if (indexB !== -1) return 1;

            // Neither are in ranked list (shouldn't happen with our filtering)
            return 0;
          });

          // Combine: OTHER projects FIRST, then NEW projects, then RANKED projects LAST
          const combinedSortedData = [
            ...sortedOtherProjects,
            ...sortedNewProjects,
            ...sortedRankedProjects,
          ];

          setProjects(combinedSortedData);
        } else {
          setError("Invalid data format.");
        }
      } catch (e) {
        setError(`Failed to fetch projects: ${e.message}`);
      } finally {
        setIsLoading(false);
      }
    };
    fetchAllProjects();
  }, [currentProjectId]); // Add currentProjectId as dependency

  return (
    <div style={styles.mainContainerStyle}>
      <style>
        {`
                @media (min-width: 768px) {
                    .project-responsive-grid { grid-template-columns: repeat(2, 1fr) !important; }
                }
                @media (min-width: 1024px) {
                    .project-responsive-grid { grid-template-columns: repeat(3, 1fr) !important; }
                }
                `}
      </style>

      {/* Header with Current ID Display */}

      {isLoading ? (
        <div style={{ textAlign: "center", padding: "50px", fontSize: "18px" }}>
          Loading...
        </div>
      ) : error ? (
        <div style={{ textAlign: "center", padding: "50px", color: "red" }}>
          {error}
        </div>
      ) : projects.length === 0 ? (
        <div style={styles.emptyStateStyle}>No other projects to display.</div>
      ) : (
        <div className="project-responsive-grid" style={styles.gridStyle}>
          {projects.map((project, index) => (
            <CardWithShare key={project.pid || index} project={project} />
          ))}
        </div>
      )}
    </div>
  );
};

export default ProjectCardsGrid;
